import { useEffect } from 'react';
import {
	type FieldValues,
	useController,
	useForm as useReactHookForm,
	useFormContext as useHookFormContext,
	type UseFormProps,
} from 'react-hook-form';

import { useValidationErrorGTM } from 'hooks/gtm';
import { useI18n } from 'utils/i18n';

import { useFormContext as useInternalFormContext } from './FormProvider';

export function useFormContext() {
	const formContext = useInternalFormContext();
	const hookFormContext = useHookFormContext();

	return { formContext, hookFormContext };
}

export function useValidatedController({
	defaultValue,
	disabled = false,
	name,
	pattern,
	patternErrorMessage,
	required,
	requiredErrorMessage,
}: {
	defaultValue?: unknown;
	disabled: boolean | undefined;
	name: string;
	pattern?: string;
	patternErrorMessage?: string;
	required?: boolean;
	requiredErrorMessage?: string;
}) {
	const { t } = useI18n();
	const { sendValidationErrorEvent } = useValidationErrorGTM();
	const { formContext, hookFormContext } = useFormContext();
	const { resetField } = hookFormContext;
	const { field, fieldState, formState } = useController({
		defaultValue,
		name,
		rules: {
			required: required
				? requiredErrorMessage || t('general_field_required_validation_text')
				: undefined,
			pattern: pattern
				? {
						value: new RegExp(pattern),
						message:
							patternErrorMessage || t('general_field_pattern_validation_text'),
					}
				: undefined,
		},
		disabled,
	});

	const invalid =
		fieldState.invalid && (fieldState.isTouched || formState.isSubmitted);

	// Reset a field that gets disabled after having been rendered as active.
	// Do this to avoid having errors connected to or input value inside
	// a visually disabled field. Errors and values are not a part of the form
	// when disabled, but if not reset, it might be there visually which is confusing.
	// A note here is that the reset, setup this way, might add a value to the field
	// if the top level form has a supplied default value.
	useEffect(() => {
		if (disabled) resetField(name);
	}, [disabled, name, resetField]);

	useEffect(() => {
		if (invalid) {
			sendValidationErrorEvent(
				formContext.formLocation,
				name,
				fieldState.error?.type ?? 'type_missing',
			);
		}
	}, [
		fieldState.error?.type,
		formContext.formLocation,
		invalid,
		name,
		sendValidationErrorEvent,
	]);

	return {
		value: field.value,
		invalid,
		valid: !fieldState.invalid && fieldState.isTouched,
		errorMessage: fieldState.error?.message,
		onChange: field.onChange,
		onBlur: field.onBlur,
	};
}

// TODO: this probably does not need to accept everything that the hook form
// version does. Better to add the things we need, and add defaults that we
// think are sensible.
export function useForm<
	TFieldValues extends FieldValues = FieldValues,
	TContext = unknown,
	TTransformedValues = TFieldValues,
>(props?: UseFormProps<TFieldValues, TContext, TTransformedValues>) {
	return useReactHookForm(props);
}
